import { useEffect, useRef, useState, useCallback } from 'react'

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

interface VoiceAgentConfig {
  lang: string
  voice?: string
}

interface PlaybackInfo {
  id: string
  source: AudioBufferSourceNode
  durationMs: number
  startTimer: number | null
  started: boolean
  interrupted: boolean
  startedAtMs: number
  responseId?: string
  itemId?: string
  outputIndex?: number
  contentIndex?: number
  chunkIndex: number
}

interface PlaybackChunk {
  data: Float32Array
  responseId?: string
  itemId?: string
  outputIndex?: number
  contentIndex?: number
  chunkIndex: number
}

const DEFAULT_CONFIG: VoiceAgentConfig = {
  lang: 'de',
}

const getWsUrl = (config: VoiceAgentConfig) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  const params = new URLSearchParams({ lang: config.lang })
  if (config.voice) params.set('voice', config.voice)
  return `${protocol}//${host}/ws/voice?${params.toString()}`
}

const getVoiceConfig = async () => {
  try {
    const response = await fetch('/api/voice/config', { credentials: 'same-origin' })
    if (!response.ok) return { recording_enabled: true }
    return response.json() as Promise<{ recording_enabled: boolean }>
  } catch {
    return { recording_enabled: true }
  }
}

const getGreetingPrompt = (lang: string, recordingEnabled: boolean) => {
  if (lang === 'de') {
    if (!recordingEnabled) {
      return 'Der Anruf hat begonnen. Begruesse den Besucher auf Deutsch als Sprachassistent des Activity Park in Paderborn und frage kurz, wie du helfen kannst.'
    }
    return 'Der Anruf hat begonnen. Begruesse den Besucher auf Deutsch als Sprachassistent des Activity Park in Paderborn. Sage kurz, dass das Gespraech zur Qualitaetssicherung aufgezeichnet wird, und frage dann, wie du helfen kannst.'
  }

  if (!recordingEnabled) {
    return 'Звонок начался. Поприветствуй посетителя на русском как голосовой помощник Activity Park в Падерборне и коротко спроси, чем помочь.'
  }
  return 'Звонок начался. Поприветствуй посетителя на русском как голосовой помощник Activity Park в Падерборне. Коротко скажи, что разговор записывается для контроля качества, и спроси, чем помочь.'
}

export function useVoiceAgent(userConfig?: Partial<VoiceAgentConfig>) {
  const config = { ...DEFAULT_CONFIG, ...userConfig }

  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [isRecording, setIsRecording] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [conversation, setConversation] = useState<Message[]>([])
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const connectPromiseRef = useRef<Promise<WebSocket> | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const workletKeepAliveGainRef = useRef<GainNode | null>(null)
  const playbackContextRef = useRef<AudioContext | null>(null)
  const currentPlaybackSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const currentPlaybackInfoRef = useRef<PlaybackInfo | null>(null)
  const audioWorkletsLoadedRef = useRef(false)
  const liveRecorderNodeRef = useRef<AudioWorkletNode | null>(null)
  const liveMicGainRef = useRef<GainNode | null>(null)
  const liveAssistantGainRef = useRef<GainNode | null>(null)
  const liveKeepAliveGainRef = useRef<GainNode | null>(null)
  const liveRecordingEnabledRef = useRef(false)
  const liveRecordingStartedRef = useRef(false)
  const liveFlushPendingRef = useRef(false)
  const liveChunkCountRef = useRef(0)
  const liveFrameCountRef = useRef(0)
  const audioQueueRef = useRef<PlaybackChunk[]>([])
  const isPlayingRef = useRef(false)
  const ignoreIncomingAudioRef = useRef(false)
  const recordingActiveRef = useRef(false)
  const currentTranscriptRef = useRef('')
  const transcriptFrameRef = useRef<number | null>(null)
  const sessionStartedRef = useRef(false)
  const configRef = useRef(config)
  const serverErrorRef = useRef(false)
  const manualCloseRef = useRef(false)
  const localBargeInFramesRef = useRef(0)
  const endCallRequestedRef = useRef(false)
  const endAfterAssistantResponseRef = useRef(false)
  const endCallTimerRef = useRef<number | null>(null)
  const scheduleEndCallRef = useRef<(() => void) | null>(null)
  const disconnectRef = useRef<(() => void) | null>(null)
  const playbackStartTimestampRef = useRef<number>(0)
  const scheduledEndRef = useRef(0)
  const falseSpeechGuardRef = useRef(0)
  const diagChunkIdxRef = useRef(0)
  const diagChunkCountRef = useRef(0)
  const diagGapCountRef = useRef(0)
  const diagLastOnendedRef = useRef(0)

  useEffect(() => {
    configRef.current = config
  }, [config])

  const flushTranscript = useCallback(() => {
    transcriptFrameRef.current = null
    setTranscript(currentTranscriptRef.current)
  }, [])

  const resetTranscript = useCallback(() => {
    currentTranscriptRef.current = ''
    if (transcriptFrameRef.current) {
      cancelAnimationFrame(transcriptFrameRef.current)
      transcriptFrameRef.current = null
    }
    setTranscript('')
  }, [])

  const sendClientEvent = useCallback((payload: Record<string, unknown>) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify(payload))
  }, [])

  const buildPlaybackPayload = useCallback((type: string, info: PlaybackInfo, extra: Record<string, unknown> = {}) => ({
    type,
    playback_id: info.id,
    response_id: info.responseId,
    item_id: info.itemId,
    output_index: info.outputIndex,
    content_index: info.contentIndex,
    chunk_index: info.chunkIndex,
    ...extra,
  }), [])

  const sendLiveRecordingStopped = useCallback(() => {
    if (!liveRecordingStartedRef.current) return
    sendClientEvent({
      type: 'recording.live.stopped',
      chunks: liveChunkCountRef.current,
      frames: liveFrameCountRef.current,
    })
    liveRecordingStartedRef.current = false
  }, [sendClientEvent])

  const sendPlaybackInterrupted = useCallback((reason: string) => {
    const info = currentPlaybackInfoRef.current
    if (!info || info.interrupted) return

    info.interrupted = true
    if (info.startTimer !== null) {
      window.clearTimeout(info.startTimer)
      info.startTimer = null
    }

    sendClientEvent(buildPlaybackPayload('assistant.playback.interrupted', info, {
      reason,
      played_ms: info.started ? Math.max(0, Math.round(performance.now() - info.startedAtMs)) : 0,
      duration_ms: Math.round(info.durationMs),
    }))
  }, [buildPlaybackPayload, sendClientEvent])

  const playNextInQueue = useCallback(() => {
    if (isPlayingRef.current) return
    if (audioQueueRef.current.length === 0) return

    isPlayingRef.current = true

    if (!playbackContextRef.current) {
      playbackContextRef.current = new AudioContext({ sampleRate: 24000 })
    }

    const ctx = playbackContextRef.current
    const chunk = audioQueueRef.current.shift()!
    const data = chunk.data

    const buffer = ctx.createBuffer(1, data.length, 24000)
    buffer.getChannelData(0).set(data)

    const source = ctx.createBufferSource()
    currentPlaybackSourceRef.current = source
    source.buffer = buffer
    source.connect(ctx.destination)
    if (liveAssistantGainRef.current) {
      source.connect(liveAssistantGainRef.current)
    }

    const duration = data.length / 24000
    const now = ctx.currentTime
    const startTime = Math.max(now, scheduledEndRef.current)
    const gap = startTime - scheduledEndRef.current

    if (scheduledEndRef.current > 0 && gap > 0.005) {
      diagGapCountRef.current += 1
      console.log(`[Diag] GAP#${diagGapCountRef.current}: ${Math.round(gap * 1000)}ms | q=${audioQueueRef.current.length} | chunk#${diagChunkIdxRef.current}`)
    }

    const playbackId = `${Date.now()}-${diagChunkIdxRef.current}`
    const startDelayMs = Math.max(0, Math.round((startTime - now) * 1000))
    const playbackInfo: PlaybackInfo = {
      id: playbackId,
      source,
      durationMs: duration * 1000,
      startTimer: null,
      started: false,
      interrupted: false,
      startedAtMs: performance.now() + startDelayMs,
      responseId: chunk.responseId,
      itemId: chunk.itemId,
      outputIndex: chunk.outputIndex,
      contentIndex: chunk.contentIndex,
      chunkIndex: chunk.chunkIndex,
    }
    playbackInfo.startTimer = window.setTimeout(() => {
      if (currentPlaybackInfoRef.current !== playbackInfo || playbackInfo.interrupted) return
      playbackInfo.started = true
      playbackInfo.startedAtMs = performance.now()
      sendClientEvent(buildPlaybackPayload('assistant.playback.started', playbackInfo, {
        duration_ms: Math.round(playbackInfo.durationMs),
      }))
    }, startDelayMs)
    currentPlaybackInfoRef.current = playbackInfo

    source.start(startTime)
    scheduledEndRef.current = startTime + duration
    diagChunkIdxRef.current += 1

    if (playbackStartTimestampRef.current === 0) {
      playbackStartTimestampRef.current = Date.now()
    }

    source.onended = () => {
      const playbackInfo = currentPlaybackInfoRef.current
      if (playbackInfo?.source === source) {
        if (playbackInfo.startTimer !== null) {
          window.clearTimeout(playbackInfo.startTimer)
          playbackInfo.startTimer = null
        }
        if (!playbackInfo.interrupted) {
          sendClientEvent(buildPlaybackPayload('assistant.playback.ended', playbackInfo, {
            played_ms: Math.round(playbackInfo.durationMs),
            duration_ms: Math.round(playbackInfo.durationMs),
          }))
        }
        currentPlaybackInfoRef.current = null
      }
      if (currentPlaybackSourceRef.current === source) {
        currentPlaybackSourceRef.current = null
      }
      isPlayingRef.current = false
      if (audioQueueRef.current.length > 0) {
        diagLastOnendedRef.current = 0
        playNextInQueue()
      } else {
        playbackStartTimestampRef.current = 0
        scheduledEndRef.current = 0
        if (endCallRequestedRef.current || endAfterAssistantResponseRef.current) {
          scheduleEndCallRef.current?.()
        }
      }
    }
  }, [buildPlaybackPayload, sendClientEvent])

  const playAudioDelta = useCallback((base64Audio: string, sourceMessage?: any) => {
    try {
      const binaryString = atob(base64Audio)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      const pcm16 = new Int16Array(bytes.buffer)
      const float32 = new Float32Array(pcm16.length)
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = Math.max(-1, Math.min(1, pcm16[i] / 32768.0))
      }

      audioQueueRef.current.push({
        data: float32,
        responseId: sourceMessage?.response_id,
        itemId: sourceMessage?.item_id,
        outputIndex: sourceMessage?.output_index,
        contentIndex: sourceMessage?.content_index,
        chunkIndex: diagChunkCountRef.current,
      })
      if (audioQueueRef.current.length >= 3) {
        setTimeout(() => playNextInQueue(), 10)
      }
    } catch (e) {
      console.error('[VoiceAgent] Ошибка воспроизведения:', e)
    }
  }, [playNextInQueue])

  const stopPlayback = useCallback((ignoreIncomingAudio = false) => {
    audioQueueRef.current = []
    ignoreIncomingAudioRef.current = ignoreIncomingAudio
    isPlayingRef.current = false
    playbackStartTimestampRef.current = 0
    scheduledEndRef.current = 0

    if (currentPlaybackSourceRef.current) {
      sendPlaybackInterrupted(ignoreIncomingAudio ? 'barge_in' : 'stop')
      try {
        currentPlaybackSourceRef.current.stop()
        currentPlaybackSourceRef.current.disconnect()
      } catch {
        // Источник мог уже завершиться.
      }
      currentPlaybackSourceRef.current = null
    }

    setIsSpeaking(false)
  }, [sendPlaybackInterrupted])

  const scheduleEndCall = useCallback(() => {
    if (endCallTimerRef.current) {
      window.clearTimeout(endCallTimerRef.current)
    }
    endCallTimerRef.current = window.setTimeout(() => {
      endCallTimerRef.current = null
      if (isPlayingRef.current || audioQueueRef.current.length > 0 || currentPlaybackSourceRef.current) {
        scheduleEndCallRef.current?.()
        return
      }
      disconnectRef.current?.()
    }, 350)
  }, [])

  useEffect(() => {
    scheduleEndCallRef.current = scheduleEndCall
  }, [scheduleEndCall])

  const stopRecordingInternal = useCallback(() => {
    recordingActiveRef.current = false

    if (liveRecorderNodeRef.current && !liveFlushPendingRef.current) {
      const liveRecorderNode = liveRecorderNodeRef.current
      const liveMicGain = liveMicGainRef.current
      const liveAssistantGain = liveAssistantGainRef.current
      const liveKeepAliveGain = liveKeepAliveGainRef.current

      liveFlushPendingRef.current = true
      liveRecorderNode.port.postMessage({ type: 'stop' })
      window.setTimeout(() => {
        sendLiveRecordingStopped()
        liveRecorderNode.disconnect()
        liveMicGain?.disconnect()
        liveAssistantGain?.disconnect()
        liveKeepAliveGain?.disconnect()
        if (liveRecorderNodeRef.current === liveRecorderNode) liveRecorderNodeRef.current = null
        if (liveMicGainRef.current === liveMicGain) liveMicGainRef.current = null
        if (liveAssistantGainRef.current === liveAssistantGain) liveAssistantGainRef.current = null
        if (liveKeepAliveGainRef.current === liveKeepAliveGain) liveKeepAliveGainRef.current = null
        liveRecordingEnabledRef.current = false
        liveChunkCountRef.current = 0
        liveFrameCountRef.current = 0
        liveFlushPendingRef.current = false
      }, 140)
    } else if (!liveRecorderNodeRef.current) {
      sendLiveRecordingStopped()
      liveRecordingEnabledRef.current = false
      liveChunkCountRef.current = 0
      liveFrameCountRef.current = 0
      liveFlushPendingRef.current = false
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }
    if (workletKeepAliveGainRef.current) {
      workletKeepAliveGainRef.current.disconnect()
      workletKeepAliveGainRef.current = null
    }
    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect()
      audioSourceRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current !== playbackContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop())
      mediaStreamRef.current = null
    }

    setIsRecording(false)
  }, [sendLiveRecordingStopped])

  const handleServerMessage = useCallback((msg: any) => {
    switch (msg.type) {
      case 'session.created':
        console.log('[VoiceAgent] XAI сессия создана')
        break

      case 'session.updated':
        console.log('[VoiceAgent] XAI сессия настроена')
        break

      case 'response.created':
        diagChunkCountRef.current = 0
        diagGapCountRef.current = 0
        diagChunkIdxRef.current = 0
        ignoreIncomingAudioRef.current = false
        break

      case 'input_audio_buffer.speech_started':
        if (isPlayingRef.current || Date.now() - falseSpeechGuardRef.current > 1000) {
          stopPlayback(true)
          resetTranscript()
        }
        break

      case 'response.function_call_arguments.done':
        if (msg.name === 'end_call') {
          console.log('[VoiceAgent] end_call tool requested')
          endCallRequestedRef.current = true
          if (!isPlayingRef.current) {
            scheduleEndCall()
          }
        }
        break

      case 'response.output_item.done':
        if (msg.item?.type === 'function_call' && msg.item?.name === 'end_call') {
          console.log('[VoiceAgent] end_call output item received')
          endCallRequestedRef.current = true
          if (!isPlayingRef.current) {
            scheduleEndCall()
          }
        }
        break

      case 'response.audio.delta':
      case 'response.output_audio.delta': {
        if (ignoreIncomingAudioRef.current) break
        const audioData = msg.delta
        if (audioData) {
          diagChunkCountRef.current += 1
          playAudioDelta(audioData, msg)
        }
        setIsSpeaking(true)
        break
      }

      case 'response.audio.done':
      case 'response.output_audio.done':
        playNextInQueue()
        console.log(`[Diag] ${msg.type} | chunks=${diagChunkCountRef.current} gaps=${diagGapCountRef.current} queue=${audioQueueRef.current.length} Playing=${isPlayingRef.current}`)
        ignoreIncomingAudioRef.current = false
        setIsSpeaking(false)
        falseSpeechGuardRef.current = Date.now()
        if (endCallRequestedRef.current || endAfterAssistantResponseRef.current) {
          scheduleEndCall()
        }
        break
      case 'response.done':
        console.log(`[Diag] ${msg.type} | chunks=${diagChunkCountRef.current} gaps=${diagGapCountRef.current} queue=${audioQueueRef.current.length} Playing=${isPlayingRef.current}`)
        ignoreIncomingAudioRef.current = false
        setIsSpeaking(false)
        falseSpeechGuardRef.current = Date.now()
        if (endCallRequestedRef.current || endAfterAssistantResponseRef.current) {
          scheduleEndCall()
        }
        break

      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
      case 'response.text.delta': {
        if (ignoreIncomingAudioRef.current) break
        const delta = msg.delta || ''
        currentTranscriptRef.current += delta
        if (!transcriptFrameRef.current) {
          transcriptFrameRef.current = requestAnimationFrame(flushTranscript)
        }
        break
      }

      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
      case 'response.text.done': {
        const finalText = currentTranscriptRef.current.trim()
        if (transcriptFrameRef.current) {
          cancelAnimationFrame(transcriptFrameRef.current)
          transcriptFrameRef.current = null
        }
        if (finalText && !ignoreIncomingAudioRef.current) {
          setConversation(prev => [
            ...prev,
            { role: 'assistant', content: finalText, timestamp: Date.now() }
          ])
        }
        currentTranscriptRef.current = ''
        setTranscript('')
        break
      }

      case 'conversation.item.input_audio_transcription.completed': {
        const text = msg.transcript || ''
        if (text.trim()) {
          if (looksLikeCallEnding(text)) {
            endAfterAssistantResponseRef.current = true
          }
          setConversation(prev => [
            ...prev,
            { role: 'user', content: text.trim(), timestamp: Date.now() }
          ])
        }
        break
      }

      case 'error': {
        const message = msg.error?.message || 'Ошибка API'
        if (message.includes('Cancellation failed: no active response found')) break
        console.error('[VoiceAgent] Ошибка XAI:', msg.error)
        setError(message)
        setStatus('error')
        serverErrorRef.current = true
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.close()
        }
        break
      }

      default:
        break
    }
  }, [flushTranscript, playAudioDelta, resetTranscript, scheduleEndCall, stopPlayback])

  const connect = useCallback((): Promise<WebSocket> => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return Promise.resolve(wsRef.current)
    }
    if (connectPromiseRef.current) {
      return connectPromiseRef.current
    }

    setStatus('connecting')
    setError(null)
    serverErrorRef.current = false
    manualCloseRef.current = false

    const promise = new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(getWsUrl(configRef.current))
      wsRef.current = ws

      ws.onopen = () => {
        console.log('[VoiceAgent] WebSocket подключен')
        connectPromiseRef.current = null
        setStatus('connected')
        setError(null)
        resolve(ws)
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          handleServerMessage(msg)
        } catch (e) {
          console.error('[VoiceAgent] Ошибка парсинга:', e)
        }
      }

      ws.onerror = () => {
        console.error('[VoiceAgent] WebSocket ошибка')
        connectPromiseRef.current = null
        setStatus('error')
        const message = configRef.current.lang === 'de'
          ? 'Verbindungsfehler zum Server'
          : 'Ошибка соединения с сервером'
        setError(message)
        reject(new Error(message))
      }

      ws.onclose = () => {
        console.log('[VoiceAgent] WebSocket закрыт')
        connectPromiseRef.current = null
        if (wsRef.current === ws) wsRef.current = null
        stopRecordingInternal()
        stopPlayback(false)
        resetTranscript()
        sessionStartedRef.current = false

        if (manualCloseRef.current) {
          setStatus('disconnected')
          manualCloseRef.current = false
        } else {
          setStatus(serverErrorRef.current ? 'error' : 'disconnected')
        }
      }
    })

    connectPromiseRef.current = promise
    return promise
  }, [handleServerMessage, resetTranscript, stopPlayback, stopRecordingInternal])

  const sendSessionConfigAndGreeting = useCallback((ws: WebSocket, recordingEnabled: boolean) => {
    if (sessionStartedRef.current) return

    const lang = configRef.current.lang
    ws.send(JSON.stringify({
      type: 'session.update',
      session: {
        audio: {
          input: { format: { type: 'audio/pcm', rate: 24000 } },
          output: { format: { type: 'audio/pcm', rate: 24000 } },
        },
        turn_detection: {
          type: 'server_vad',
          threshold: 0.7,
        },
      },
    }))

    ws.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: getGreetingPrompt(lang, recordingEnabled) }],
      },
    }))
    ws.send(JSON.stringify({ type: 'response.create' }))

    sessionStartedRef.current = true
  }, [])

  const startRecording = useCallback(async () => {
    if (recordingActiveRef.current) return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          sampleRate: 24000,
        }
      })
      mediaStreamRef.current = stream

      if (!playbackContextRef.current) {
        playbackContextRef.current = new AudioContext({ sampleRate: 24000 })
      }
      const audioContext = playbackContextRef.current
      audioContextRef.current = audioContext
      await audioContext.resume().catch(() => {})

      if (!audioWorkletsLoadedRef.current) {
        await audioContext.audioWorklet.addModule('/pcm16-worklet.js')
        await audioContext.audioWorklet.addModule('/live-mix-worklet.js')
        audioWorkletsLoadedRef.current = true
      }

      const source = audioContext.createMediaStreamSource(stream)
      audioSourceRef.current = source

      const workletNode = new AudioWorkletNode(audioContext, 'pcm16-processor')
      workletNodeRef.current = workletNode
      const workletKeepAliveGain = audioContext.createGain()
      workletKeepAliveGain.gain.value = 0
      workletKeepAliveGainRef.current = workletKeepAliveGain

      const voiceConfig = await getVoiceConfig()
      const ws = await connect()

      workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
        if (!recordingActiveRef.current) return

        const isSpeakingOrPlaying = isPlayingRef.current || isSpeaking
        const hasPassedGracePeriod = playbackStartTimestampRef.current > 0 && (Date.now() - playbackStartTimestampRef.current >= 80)

        if (isSpeakingOrPlaying && hasPassedGracePeriod && isLikelyUserSpeech(event.data)) {
          localBargeInFramesRef.current += 1
          if (localBargeInFramesRef.current >= 2) {
            stopPlayback(true)
            resetTranscript()
          }
        } else {
          localBargeInFramesRef.current = 0
        }

        wsRef.current.send(JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: arrayBufferToBase64(event.data),
        }))
      }

      source.connect(workletNode)
      workletNode.connect(workletKeepAliveGain)
      workletKeepAliveGain.connect(audioContext.destination)

      if (voiceConfig.recording_enabled) {
        const liveRecorderNode = new AudioWorkletNode(audioContext, 'live-mix-recorder', {
          numberOfInputs: 2,
          numberOfOutputs: 1,
          outputChannelCount: [1],
        })
        const liveMicGain = audioContext.createGain()
        const liveAssistantGain = audioContext.createGain()
        const liveKeepAliveGain = audioContext.createGain()
        liveKeepAliveGain.gain.value = 0

        liveRecorderNode.port.onmessage = (event: MessageEvent<{ type?: string; buffer?: ArrayBuffer; frames?: number }>) => {
          const isDraining = liveFlushPendingRef.current
          if ((!recordingActiveRef.current && !isDraining) || (!liveRecordingEnabledRef.current && !isDraining)) return
          if (event.data?.type !== 'chunk' || !event.data.buffer) return

          const frames = event.data.frames || 0
          liveChunkCountRef.current += 1
          liveFrameCountRef.current += frames
          sendClientEvent({
            type: 'recording.live.chunk',
            audio: arrayBufferToBase64(event.data.buffer),
            frames,
          })
        }

        source.connect(liveMicGain)
        liveMicGain.connect(liveRecorderNode, 0, 0)
        liveAssistantGain.connect(liveRecorderNode, 0, 1)
        liveRecorderNode.connect(liveKeepAliveGain)
        liveKeepAliveGain.connect(audioContext.destination)

        liveRecorderNodeRef.current = liveRecorderNode
        liveMicGainRef.current = liveMicGain
        liveAssistantGainRef.current = liveAssistantGain
        liveKeepAliveGainRef.current = liveKeepAliveGain
        liveRecordingEnabledRef.current = true
        liveRecordingStartedRef.current = true
        sendClientEvent({
          type: 'recording.live.started',
          sample_rate: audioContext.sampleRate,
        })
      }

      recordingActiveRef.current = true
      ignoreIncomingAudioRef.current = false
      setIsRecording(true)
      resetTranscript()

      sendSessionConfigAndGreeting(ws, voiceConfig.recording_enabled)
      console.log('[VoiceAgent] Звонок начат')
    } catch (err: any) {
      console.error('[VoiceAgent] Ошибка старта звонка:', err)
      stopRecordingInternal()
      manualCloseRef.current = true
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      setStatus('disconnected')
      setError(configRef.current.lang === 'de'
        ? 'Mikrofon oder Verbindung konnte nicht gestartet werden'
        : 'Не удалось запустить микрофон или соединение')
    }
  }, [connect, resetTranscript, sendSessionConfigAndGreeting, stopRecordingInternal])

  const disconnect = useCallback(() => {
    manualCloseRef.current = true
    stopRecordingInternal()
    stopPlayback(false)
    resetTranscript()
    sessionStartedRef.current = false
    serverErrorRef.current = false
    endCallRequestedRef.current = false
    endAfterAssistantResponseRef.current = false
    localBargeInFramesRef.current = 0
    connectPromiseRef.current = null
    playbackStartTimestampRef.current = 0
    sendPlaybackInterrupted('disconnect')

    if (endCallTimerRef.current) {
      window.clearTimeout(endCallTimerRef.current)
      endCallTimerRef.current = null
    }

    if (playbackContextRef.current) {
      const contextToClose = playbackContextRef.current
      const closeContext = () => {
        contextToClose.close().catch(() => {})
      }
      if (liveFlushPendingRef.current) {
        window.setTimeout(closeContext, 150)
      } else {
        closeContext()
      }
      playbackContextRef.current = null
      audioContextRef.current = null
      audioWorkletsLoadedRef.current = false
    }

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    setStatus('disconnected')
    console.log('[VoiceAgent] Звонок завершён')
  }, [resetTranscript, sendPlaybackInterrupted, stopPlayback, stopRecordingInternal])

  useEffect(() => {
    disconnectRef.current = disconnect
  }, [disconnect])

  const stopRecording = useCallback(() => {
    disconnect()
  }, [disconnect])

  const interrupt = useCallback(() => {
    stopPlayback(true)
    resetTranscript()
  }, [resetTranscript, stopPlayback])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    status,
    isRecording,
    isSpeaking,
    transcript,
    conversation,
    error,
    startRecording,
    stopRecording,
    connect,
    disconnect,
    interrupt,
  }
}

function arrayBufferToBase64(buffer: ArrayBufferLike): string {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 8192
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.byteLength))
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

function isLikelyUserSpeech(buffer: ArrayBufferLike): boolean {
  const samples = new Int16Array(buffer as ArrayBuffer)
  if (samples.length === 0) return false

  let sumSquares = 0
  let peak = 0
  for (let i = 0; i < samples.length; i++) {
    const normalized = samples[i] / 32768
    sumSquares += normalized * normalized
    peak = Math.max(peak, Math.abs(normalized))
  }

  const rms = Math.sqrt(sumSquares / samples.length)
  return rms > 0.04 || peak > 0.2
}

function looksLikeCallEnding(text: string): boolean {
  const normalized = text.toLowerCase().trim()
  return [
    /до свидан/,
    /\bпока\b/,
    /все спасибо/,
    /всё спасибо/,
    /спасибо[,.\s]+все/,
    /спасибо[,.\s]+всё/,
    /больше вопросов нет/,
    /вопросов больше нет/,
    /нет вопросов/,
    /на этом все/,
    /на этом всё/,
    /goodbye/,
    /bye\b/,
    /that'?s all/,
    /no more questions/,
    /tsch[uü]ss/,
    /auf wiedersehen/,
    /das war'?s/,
    /keine fragen/,
    /vielen dank.*(tsch|wiedersehen|alles)/,
  ].some(pattern => pattern.test(normalized))
}
